import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { getOAuthClients, renewOAuthClient, revokeOAuthClient, type OAuthClientRow } from '../lib/api';
import { getMcpRegisterEndpoint, getMcpTokenEndpoint } from '../lib/mcp';
import { expiryLabel, expiryNoticesFor, type ExpiryNoticeTone } from '../lib/oauth-client-expiry';
import useSWR, { mutate } from 'swr';

const NOTICE_STYLES: Readonly<Record<ExpiryNoticeTone, string>> = {
  critical: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  info: 'border-blue-200 bg-blue-50 text-blue-900',
};

export function OAuthClientsPage() {
  const { user, session } = useAuth();
  const mcpRegisterEndpoint = getMcpRegisterEndpoint();
  const mcpTokenEndpoint = getMcpTokenEndpoint();

  const { data: clients, error: fetchError } = useSWR(
    user && session ? ['oauth-clients', user.id] : null,
    async () => {
      const response = await getOAuthClients(session!.access_token);
      const body = (await response.json().catch(() => null)) as {
        clients?: OAuthClientRow[];
        error?: string;
      } | null;

      if (!response.ok) {
        throw new Error(body?.error ?? 'Failed to load OAuth clients.');
      }

      return body?.clients ?? [];
    },
  );

  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [renewMessage, setRenewMessage] = useState<string | null>(null);

  // FEAT-010: extend a client this tenant registered to now + 90 days.
  const handleRenew = async (clientId: string): Promise<void> => {
    if (!session || renewingId) return;
    setRenewingId(clientId);
    setRenewMessage(null);
    try {
      const res = await renewOAuthClient(session.access_token, clientId);
      const body = (await res.json().catch(() => ({}))) as {
        status?: string;
        expires_at?: string;
        error?: string;
        error_description?: string;
      };
      if (!res.ok) {
        alert(body.error_description || body.error || 'Failed to renew client. Please try again.');
      } else if (body.expires_at) {
        const until = new Date(body.expires_at).toLocaleDateString();
        setRenewMessage(
          body.status === 'unchanged'
            ? `This client is already renewed until ${until}.`
            : `Client renewed until ${until}.`,
        );
      }
    } catch (err) {
      console.error('Failed to renew OAuth client:', err);
      alert('Could not reach the server. Please try again.');
    } finally {
      setRenewingId(null);
      void mutate(['oauth-clients', user!.id]);
    }
  };

  const notices = clients ? expiryNoticesFor(clients) : [];

  const handleRevoke = async (clientId: string): Promise<void> => {
    if (!session) return;

    const confirmed = window.confirm(
      'Revoke this application\'s access to your account? It will no longer be able to refresh its ' +
        'access; any access token it already holds stops working within 15 minutes.',
    );
    if (!confirmed) return;

    try {
      const res = await revokeOAuthClient(session.access_token, clientId);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; error_description?: string };
        alert(body.error_description || body.error || 'Failed to revoke client. Please try again.');
      }
    } catch (err) {
      console.error('Failed to revoke OAuth client:', err);
      alert('Could not reach the server. Please try again.');
    } finally {
      void mutate(['oauth-clients', user!.id]);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">OAuth Clients</h2>

      {fetchError && <div className="mb-4 text-red-600">Failed to load OAuth clients.</div>}

      {renewMessage && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900" role="status">
          {renewMessage}
        </div>
      )}

      {notices.length > 0 && (
        <div className="mb-6 space-y-2" role="status" aria-label="OAuth client expiry warnings">
          {notices.map((notice) => (
            <div
              key={notice.clientId}
              className={`flex flex-col gap-2 rounded-lg border p-4 text-sm sm:flex-row sm:items-center sm:justify-between ${NOTICE_STYLES[notice.tone]}`}
            >
              <p>{notice.message}</p>
              {notice.renewable && (
                <button
                  onClick={() => void handleRenew(notice.clientId)}
                  disabled={renewingId !== null}
                  className="shrink-0 rounded-md bg-white px-3 py-1.5 font-medium shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
                >
                  {renewingId === notice.clientId ? 'Renewing…' : 'Renew'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <h3 className="text-base font-semibold text-slate-900">How to use OAuth with MCP</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Register your MCP client to get a client_id, then implement the PKCE OAuth 2.1 flow to get tokens.
              Exchange tokens at the token endpoint, then send Bearer tokens on each MCP request.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Register Client Endpoint
          </p>
          <code className="mt-2 block break-all text-sm text-slate-900">{mcpRegisterEndpoint}</code>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Token Exchange Endpoint
          </p>
          <code className="mt-2 block break-all text-sm text-slate-900">{mcpTokenEndpoint}</code>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Auth Header</p>
          <code className="mt-2 block text-sm text-slate-900">Authorization: Bearer YOUR_OAUTH_ACCESS_TOKEN</code>
        </div>

        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p>
            <strong>PKCE (Proof Key for Code Exchange)</strong> is required for all OAuth clients. Your client
            generates a code_verifier, creates a code_challenge via SHA-256, and exchanges the authorization code for
            tokens.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="font-semibold mb-4">Register a New OAuth Client</h3>
        <div className="bg-slate-50 p-4 rounded-lg">
          <p className="text-sm text-slate-600 mb-3">
            Send a POST request to register a new OAuth client for your MCP integration:
          </p>
          <pre className="overflow-x-auto text-xs bg-white border border-slate-200 p-3 rounded">
            {`curl -X POST ${mcpRegisterEndpoint} \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_name": "My MCP Client",
    "redirect_uris": ["https://myapp.example.com/callback"]
  }'`}
          </pre>
          <p className="text-xs text-slate-500 mt-3">
            You'll receive a client_id and registration_access_token. Save the token — it's only shown once.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Unverified clients expire 90 days after registration, or after 30 days without a successful token
            grant, whichever is later, and keep working for a 7-day grace period after that. Only clients
            registered while signed in to this dashboard are listed as yours and can be renewed here.
          </p>
        </div>
      </div>

      {/* Registered Clients List */}
      {clients && clients.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="font-semibold">Your Clients and Authorized Apps</h3>
            <p className="text-sm text-slate-600 mt-1">
              OAuth clients you registered from this dashboard, plus every app you have authorized to access
              your account. Revoke anything you do not recognise.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3">Client Name</th>
                <th className="text-left px-6 py-3">Client ID</th>
                <th className="text-left px-6 py-3">Redirect URIs</th>
                <th className="text-left px-6 py-3">Created</th>
                <th className="text-left px-6 py-3">Expires</th>
                <th className="text-right px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.client_id} className="border-t">
                  <td className="px-6 py-3 font-medium">
                    <div>{client.client_name}</div>
                    <div className="mt-1 flex flex-wrap gap-1 text-xs font-normal">
                      <span className={client.is_verified ? 'text-green-700' : 'text-amber-700'}>
                        {client.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                      <span className="text-slate-500">
                        {client.registered_by_you ? '· registered by you' : '· authorized app'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3 font-mono text-xs text-slate-600">{client.client_id}</td>
                  <td className="px-6 py-3 text-xs">
                    {client.redirect_uris.map((uri) => (
                      <div key={uri} className="text-slate-600">
                        {uri}
                      </div>
                    ))}
                  </td>
                  <td className="px-6 py-3 text-gray-500">
                    {new Date(client.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-gray-500">{expiryLabel(client)}</td>
                  <td className="px-6 py-3 text-right">
                    {client.renewable && (
                      <button
                        onClick={() => void handleRenew(client.client_id)}
                        disabled={renewingId !== null}
                        className="mr-4 text-blue-700 hover:underline text-sm disabled:opacity-50"
                      >
                        {renewingId === client.client_id ? 'Renewing…' : 'Renew'}
                      </button>
                    )}
                    <button
                      onClick={() => void handleRevoke(client.client_id)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {clients && clients.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-dashed">
          <p className="text-gray-600">No registered clients or authorized applications.</p>
          <p className="text-sm text-gray-500 mt-2">Register your first client using the endpoint above.</p>
        </div>
      )}
    </div>
  );
}
